// MARK://dhk8/pos/pages/settings/PrintingDiagnosticsPage
// /diagnostico — Diagnóstico de impresión, accesible a TODOS los roles (red de
// seguridad). A diferencia de /settings/printing (config completa: impresoras,
// auto-print, cajón — solo gerente/cajero/admin), esta pantalla es de SOLO
// VERIFICAR + PROBAR + RECUPERAR, para que cualquiera (mesero incluido) pueda:
//   · ver si la impresión funciona en ESTA terminal (estado bridge/estación),
//   · hacer una impresión de prueba rápida,
//   · si la terminal está en "Vía nube" y no quedó vinculada (localStorage vacío
//     o perdido), re-vincularla tecleando el código de estación — sin esperar a
//     un admin,
//   · ver los últimos trabajos y reintentar.
//
// No expone la config del HOST (impresoras/auto-print/cajón) — eso sigue en
// /settings/printing, solo para roles con permiso. Reusa los mismos clientes.

import { useState, useCallback, useEffect } from 'react'
import {
  Stethoscope, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Cloud, Server,
  TestTube, Link2, Loader2, RotateCw, Clock,
} from 'lucide-react'
import { useBridgeStatus } from '../../hooks/useBridgeStatus.js'
import { printReceipt, type CanonicalReceipt } from '../../lib/bridge/bridge-client.js'
import { resolveStation, listJobs, retryJob, type RelayJob } from '../../lib/bridge/relay-client.js'
import { trackRelayJob } from '../../lib/bridge/relay-job-tracker.js'
import { usePrintingConfigStore, pushPrintHistory } from '../../lib/bridge/printing-config-store.js'

/** Ticket de diagnóstico mínimo — no requiere venta; solo verifica que el papel sale. */
function buildDiagReceipt(): CanonicalReceipt {
  return {
    encoding: 'cp858',
    width: 42,
    header: [
      { text: 'NINTAIGO', align: 'center', bold: true, size: 'double' },
      { text: 'Diagnostico de impresion', align: 'center' },
    ],
    footer: [
      { text: new Date().toLocaleString('es-MX'), align: 'center' },
      { text: 'Si ves este ticket, la impresion funciona', align: 'center' },
    ],
    cut: 'partial',
  }
}

export default function PrintingDiagnosticsPage() {
  const { status, refresh, lastCheckedAt } = useBridgeStatus()
  const bridge = usePrintingConfigStore((s) => s.bridge)
  const setBridge = usePrintingConfigStore((s) => s.setBridge)

  const isRelay = bridge.mode === 'relay'
  const linked = isRelay ? !!bridge.stationId : true

  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null)
  const flash = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ msg, kind }); setTimeout(() => setToast(null), 2800)
  }

  // ── Probar impresión ───────────────────────────────────────────────────────
  const [testing, setTesting] = useState(false)
  async function handleTest() {
    setTesting(true)
    try {
      const r = await printReceipt(buildDiagReceipt(), { kind: 'receipt' })
      if (isRelay && r.jobId) {
        trackRelayJob(r.jobId, 'Diagnóstico', 'receipt')
        flash('Prueba enviada a la estación')
        void loadJobs()
      } else {
        pushPrintHistory('diagnostico', 'ok')
        flash('Prueba enviada a la impresora')
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo imprimir'
      pushPrintHistory('diagnostico', 'error', msg)
      flash(msg, 'err')
    } finally { setTesting(false) }
  }

  // ── Vincular por código (recuperación cuando relay no quedó vinculado) ───────
  const [code, setCode] = useState('')
  const [linking, setLinking] = useState(false)
  const [linkErr, setLinkErr] = useState<string | null>(null)
  const canLink = code.trim().length >= 4 && !linking
  async function handleLink() {
    if (!canLink) return
    setLinking(true); setLinkErr(null)
    try {
      const { stationId, name } = await resolveStation(code.trim())
      setBridge({ stationId, stationName: name })
      flash(`Vinculado a "${name}"`)
      void refresh()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo vincular'
      setLinkErr(/404|not.?found/i.test(msg)
        ? 'Código no encontrado. Verifica que sea el de esta cuenta y entorno (pruebas/vivo).'
        : msg)
    } finally { setLinking(false) }
  }

  // ── Trabajos recientes (solo relay vinculado) ───────────────────────────────
  const [jobs, setJobs] = useState<RelayJob[] | null>(null)
  const [retrying, setRetrying] = useState<string | null>(null)
  const stationId = bridge.stationId
  const loadJobs = useCallback(async (signal?: AbortSignal) => {
    if (!isRelay || !stationId) { setJobs(null); return }
    try { setJobs(await listJobs(stationId, 8, signal)) } catch { if (!signal?.aborted) setJobs([]) }
  }, [isRelay, stationId])
  useEffect(() => {
    const ctrl = new AbortController()
    void loadJobs(ctrl.signal)
    return () => ctrl.abort()
  }, [loadJobs])
  async function handleRetry(id: string) {
    setRetrying(id)
    try { await retryJob(id); flash('Reimpresión solicitada'); await loadJobs() }
    catch (e) { flash(e instanceof Error ? e.message : 'No se pudo reimprimir', 'err') }
    finally { setRetrying(null) }
  }

  // ── Estado visual ───────────────────────────────────────────────────────────
  const view = statusView(status)
  const offlineHost = !isRelay && status?.state === 'offline'

  return (
    <div className="dhk8-diag">
      <h1 className="dhk8-diag__h1"><Stethoscope size={18} /> Diagnóstico de impresión</h1>
      <p className="dhk8-diag__lead">
        Verifica que esta terminal imprima y haz una prueba rápida. Si algo falla, aquí lo puedes
        revisar y resolver sin esperar a un administrador.
      </p>

      {/* Estado */}
      <section className="dhk8-diag__card">
        <div className="dhk8-diag__row">
          <span className={`dhk8-diag__badge dhk8-diag__badge--${view.tone}`}>{view.icon} {view.label}</span>
          <button type="button" className="dhk8-diag__ghost" onClick={() => void refresh()}>
            <RefreshCw size={13} /> Reintentar
          </button>
        </div>
        <dl className="dhk8-diag__kv">
          <dt>Modo</dt>
          <dd>{isRelay ? <><Cloud size={13} /> Vía nube</> : <><Server size={13} /> Este equipo (USB)</>}</dd>
          {isRelay && (
            <>
              <dt>Estación</dt>
              <dd>{bridge.stationName ?? (linked ? 'Vinculada' : '— sin vincular —')}</dd>
            </>
          )}
          {lastCheckedAt && (<><dt>Revisado</dt><dd>{new Date(lastCheckedAt).toLocaleTimeString('es-MX')}</dd></>)}
        </dl>
        {view.hint && <p className="dhk8-diag__note">{view.hint}</p>}
      </section>

      {/* Recuperación: relay sin vincular → teclear código */}
      {isRelay && !linked && (
        <section className="dhk8-diag__card">
          <div className="dhk8-diag__card-title"><Link2 size={15} /> Vincular esta terminal</div>
          <p className="dhk8-diag__sub">
            Esta terminal aún no está vinculada a una estación. Teclea el código que muestra el equipo
            principal (Ajustes › Impresión › Este equipo › "Publicar esta estación en la nube").
          </p>
          <div className="dhk8-diag__linkrow">
            <input
              type="text" inputMode="text" placeholder="XXXX-XXXX"
              className="dhk8-diag__input" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleLink() }}
              autoCapitalize="characters" autoCorrect="off" spellCheck={false} maxLength={12}
              aria-label="Código de estación"
            />
            <button type="button" className="dhk8-diag__btn" disabled={!canLink} onClick={() => void handleLink()}>
              {linking ? <Loader2 size={14} className="dhk8-diag__spin" /> : <Link2 size={14} />} Vincular
            </button>
          </div>
          {linkErr && <p className="dhk8-diag__note dhk8-diag__note--err"><XCircle size={13} /> {linkErr}</p>}
        </section>
      )}

      {offlineHost && (
        <section className="dhk8-diag__card">
          <p className="dhk8-diag__note dhk8-diag__note--err">
            <XCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            El Bridge no responde en este equipo (<code>127.0.0.1:9101</code>). Pide a un administrador
            que lo instale o lo encienda, o cambia esta terminal a "Vía nube" en Ajustes › Impresión.
          </p>
        </section>
      )}

      {/* Probar impresión */}
      <section className="dhk8-diag__card">
        <div className="dhk8-diag__card-title"><TestTube size={15} /> Probar impresión</div>
        <p className="dhk8-diag__sub">Imprime un ticket de prueba para confirmar que el papel sale.</p>
        <button
          type="button" className="dhk8-diag__btn dhk8-diag__btn--primary"
          disabled={testing || (isRelay && !linked)}
          onClick={() => void handleTest()}
        >
          {testing ? <Loader2 size={15} className="dhk8-diag__spin" /> : <TestTube size={15} />}
          {testing ? 'Enviando…' : 'Imprimir prueba'}
        </button>
      </section>

      {/* Trabajos recientes (relay) */}
      {isRelay && linked && jobs !== null && jobs.length > 0 && (
        <section className="dhk8-diag__card">
          <div className="dhk8-diag__row">
            <div className="dhk8-diag__card-title"><Clock size={14} /> Trabajos recientes</div>
            <button type="button" className="dhk8-diag__ghost" onClick={() => void loadJobs()}><RefreshCw size={12} /> Actualizar</button>
          </div>
          <ul className="dhk8-diag__jobs">
            {jobs.map((j) => {
              const jv = jobStatusView(j)
              const canRetry = j.status === 'failed' || j.status === 'done'
              return (
                <li key={j.id} className="dhk8-diag__job">
                  <span className="dhk8-diag__job-when">{j.createdAt ? new Date(j.createdAt).toLocaleTimeString('es-MX') : ''}</span>
                  <span className={`dhk8-diag__job-status dhk8-diag__job-status--${jv.tone}`}>{jv.label}</span>
                  {canRetry && (
                    <button type="button" className="dhk8-diag__job-retry" disabled={retrying === j.id} onClick={() => void handleRetry(j.id)}>
                      {retrying === j.id ? <Loader2 size={11} className="dhk8-diag__spin" /> : <RotateCw size={11} />} Reimprimir
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {toast && <div className={`dhk8-diag__toast dhk8-diag__toast--${toast.kind}`}>{toast.msg}</div>}
      <style>{styles}</style>
    </div>
  )
}

// ── helpers de presentación ────────────────────────────────────────────────────

function statusView(status: ReturnType<typeof useBridgeStatus>['status']): {
  tone: 'ok' | 'warn' | 'err' | 'neutral'; label: string; icon: React.ReactNode; hint?: string
} {
  if (!status) return { tone: 'neutral', label: 'Detectando…', icon: <RefreshCw size={13} className="dhk8-diag__spin" /> }
  switch (status.state) {
    case 'online':
      return { tone: 'ok', label: 'Impresora lista (este equipo)', icon: <CheckCircle2 size={13} /> }
    case 'relay-online':
      return { tone: 'ok', label: 'Estación en línea', icon: <CheckCircle2 size={13} /> }
    case 'outdated':
      return { tone: 'warn', label: 'Bridge desactualizado', icon: <AlertTriangle size={13} />, hint: 'El equipo tiene el Bridge pero hay que actualizarlo. Avísale a un administrador.' }
    case 'relay-offline':
      return { tone: 'err', label: 'Estación fuera de línea', icon: <XCircle size={13} />, hint: 'La estación no responde. Las impresiones se encolan y saldrán cuando el equipo con el Bridge vuelva en línea.' }
    default:
      return { tone: 'err', label: 'Sin impresión', icon: <XCircle size={13} />, hint: status.reason ? `Razón: ${status.reason}` : undefined }
  }
}

function jobStatusView(job: RelayJob): { label: string; tone: 'pending' | 'progress' | 'ok' | 'retry' | 'fail' } {
  const att = job.maxAttempts ? ` (${job.attempts}/${job.maxAttempts})` : ''
  switch (job.status) {
    case 'pending': return { label: job.attempts > 0 ? `Reintentando${att}` : 'Enviado', tone: job.attempts > 0 ? 'retry' : 'pending' }
    case 'claimed': return { label: 'Imprimiendo', tone: 'progress' }
    case 'done': return { label: 'Impreso', tone: 'ok' }
    case 'failed': return { label: `Falló${att}`, tone: 'fail' }
    default: return { label: String(job.status), tone: 'pending' }
  }
}

const styles = `
  .dhk8-diag { display:flex; flex-direction:column; gap:var(--pos-space-4, 16px); max-width:680px; padding-bottom:var(--pos-space-8, 32px); }
  .dhk8-diag__h1 { display:flex; align-items:center; gap:var(--pos-space-2, 8px); margin:0; font-family:var(--font-display, system-ui); font-size:var(--pos-text-2xl, 24px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-diag__lead { margin:0; font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-secondary, #B6BFB8); line-height:1.5; }
  .dhk8-diag__card { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); padding:var(--pos-space-4, 16px); background:var(--pos-surface, #161C18); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg, 12px); }
  .dhk8-diag__row { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2, 8px); flex-wrap:wrap; }
  .dhk8-diag__card-title { display:flex; align-items:center; gap:6px; font-size:var(--pos-text-md, 15px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-diag__sub { margin:0; font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-tertiary, #7E8C82); line-height:1.4; }
  .dhk8-diag__badge { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-medium, 500); border-radius:var(--pos-radius-pill, 999px); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-diag__badge--ok { color:var(--pos-success, #8BC34A); border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 50%, transparent); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); }
  .dhk8-diag__badge--warn { color:var(--pos-warning, #F5A623); border-color:color-mix(in oklch, #F5A623 40%, transparent); background:color-mix(in oklch, #F5A623 12%, transparent); }
  .dhk8-diag__badge--err { color:var(--pos-danger, #E5484D); border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); background:color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent); }
  .dhk8-diag__badge--neutral { color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-diag__kv { display:grid; grid-template-columns:96px 1fr; gap:4px 12px; margin:0; font-size:var(--pos-text-sm, 13px); }
  .dhk8-diag__kv dt { color:var(--pos-fg-tertiary, #7E8C82); font-size:var(--pos-text-xs, 12px); }
  .dhk8-diag__kv dd { margin:0; color:var(--pos-fg, #E8EFE9); display:flex; align-items:center; gap:5px; }
  .dhk8-diag__note { margin:0; font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-tertiary, #7E8C82); line-height:1.5; }
  .dhk8-diag__note--err { color:var(--pos-danger, #E5484D); }
  .dhk8-diag__note code { font-family:var(--font-mono, ui-monospace, monospace); font-size:var(--pos-text-xs, 12px); padding:0 4px; background:var(--pos-canvas, #0F1411); border-radius:var(--pos-radius-sm, 6px); }
  .dhk8-diag__linkrow { display:flex; gap:var(--pos-space-2, 8px); flex-wrap:wrap; }
  .dhk8-diag__input { flex:1; min-width:140px; min-height:var(--pos-touch-target, 44px); padding:var(--pos-space-3, 12px); font:inherit; font-size:var(--pos-text-md, 15px); letter-spacing:.12em; text-align:center; background:var(--pos-canvas, #0F1411); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); }
  .dhk8-diag__input:focus { outline:none; border-color:var(--pos-tint, #1F8B3A); }
  .dhk8-diag__btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; min-height:var(--pos-touch-target, 44px); padding:var(--pos-space-3, 12px) var(--pos-space-4, 16px); font:inherit; font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-medium, 500); cursor:pointer; background:var(--pos-elevated, #1E2520); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); }
  .dhk8-diag__btn:hover:not(:disabled) { border-color:var(--pos-tint, #1F8B3A); }
  .dhk8-diag__btn:disabled { opacity:.45; cursor:not-allowed; }
  .dhk8-diag__btn--primary { background:var(--pos-tint, #1F8B3A); color:var(--pos-on-tint, #fff); border-color:var(--pos-tint, #1F8B3A); align-self:flex-start; }
  .dhk8-diag__ghost { display:inline-flex; align-items:center; gap:5px; padding:5px 10px; min-height:32px; font:inherit; font-size:var(--pos-text-xs, 12px); cursor:pointer; background:transparent; color:var(--pos-fg-secondary, #B6BFB8); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill, 999px); }
  .dhk8-diag__ghost:hover { border-color:var(--pos-tint, #1F8B3A); color:var(--pos-fg, #E8EFE9); }
  .dhk8-diag__jobs { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
  .dhk8-diag__job { display:grid; grid-template-columns:auto 1fr auto; gap:var(--pos-space-3, 12px); align-items:center; padding:var(--pos-space-2, 8px) var(--pos-space-3, 12px); background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); font-size:var(--pos-text-xs, 12px); }
  .dhk8-diag__job-when { color:var(--pos-fg-tertiary, #7E8C82); font-family:var(--font-mono, ui-monospace, monospace); font-variant-numeric:tabular-nums; }
  .dhk8-diag__job-status { justify-self:start; padding:2px 8px; border-radius:var(--pos-radius-pill, 999px); font-weight:var(--pos-weight-semibold, 600); font-size:var(--pos-text-xxs, 11px); white-space:nowrap; }
  .dhk8-diag__job-status--pending { color:var(--pos-fg-secondary, #B6BFB8); background:var(--pos-elevated, #1E2520); }
  .dhk8-diag__job-status--progress { color:var(--pos-info, #0091FF); background:color-mix(in oklch, var(--pos-info, #0091FF) 14%, transparent); }
  .dhk8-diag__job-status--retry { color:var(--pos-warning, #F5A623); background:color-mix(in oklch, #F5A623 14%, transparent); }
  .dhk8-diag__job-status--ok { color:var(--pos-success, #8BC34A); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); }
  .dhk8-diag__job-status--fail { color:var(--pos-danger, #E5484D); background:color-mix(in oklch, var(--pos-danger, #E5484D) 14%, transparent); }
  .dhk8-diag__job-retry { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; min-height:30px; font:inherit; font-size:var(--pos-text-xxs, 11px); cursor:pointer; background:transparent; color:var(--pos-fg-secondary, #B6BFB8); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill, 999px); }
  .dhk8-diag__job-retry:disabled { opacity:.45; cursor:not-allowed; }
  .dhk8-diag__spin { animation:dhk8-diag-spin 1.1s linear infinite; }
  @keyframes dhk8-diag-spin { to { transform:rotate(360deg); } }
  .dhk8-diag__toast { position:fixed; bottom:var(--pos-space-6, 24px); left:50%; transform:translateX(-50%); padding:var(--pos-space-3, 12px) var(--pos-space-5, 20px); font-size:var(--pos-text-sm, 13px); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill, 999px); background:var(--pos-elevated, #1E2520); box-shadow:var(--pos-shadow-3, 0 8px 20px -6px rgba(0,0,0,.35)); z-index:var(--pos-z-toast, 300); }
  .dhk8-diag__toast--ok { color:var(--pos-success, #8BC34A); border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 50%, transparent); }
  .dhk8-diag__toast--err { color:var(--pos-danger, #E5484D); border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); }
  @media (max-width: 600px) { .dhk8-diag__kv { grid-template-columns:1fr; gap:2px; } .dhk8-diag__kv dt { margin-top:6px; } }
`
